/**
 * Resource simulation: production, cargo transfer, losses (plan A2/E7).
 *
 * Pure functions over a Resources object so every path is trivially unit-testable — the
 * balance of the whole campaign rests on these numbers behaving predictably.
 */

import type { Resources, Cargo, ShiftProgress } from './types';
import { ECONOMY } from '../content/economy';
import { clamp } from './math';

/**
 * Oil accumulates continuously during a shift, scaled down when the crew is thin, and
 * stops dead at the storage cap. That cap is the pressure behind every tanker: a missed
 * collection is not just a lost sale, it idles the platform.
 */
export function tickProduction(res: Resources, dt: number, productionBonus: number): void {
  if (res.oil >= res.oilCapacity) return;
  const crewFactor = res.crew >= ECONOMY.crewProductionThreshold
    ? 1
    : Math.max(0, res.crew / ECONOMY.crewProductionThreshold);
  const perSecond = (ECONOMY.productionRate + productionBonus) / 60;
  res.oil = Math.min(res.oilCapacity, res.oil + perSecond * crewFactor * dt);
}

export interface TransferResult {
  money: number;
  oil: number;
  summary: string;
}

/**
 * Apply a friendly vessel's cargo on safe arrival. A tanker takes everything in storage
 * and pays for it; a supply ship adds what it was carrying.
 */
export function applyCargo(res: Resources, cargo: Cargo, shift: ShiftProgress): TransferResult {
  const parts: string[] = [];
  let money = 0;
  let oil = 0;

  /*
   * The tanker is paid her contract, not the clock.
   *
   * She still lifts whatever the rig has pumped — that is the fiction, and the shift
   * report still counts the barrels — but the money is a flat fee for the delivery.
   * Paying by the barrel made income a function of how long the shift ran, which meant
   * every pacing decision was silently an economy decision. See `ECONOMY`.
   */
  if (cargo.oilCollection) {
    oil = res.oil;
    res.oil = 0;
    money = ECONOMY.tankerContractFee;
    res.money += money;
    shift.earned += money;
    shift.oilDelivered += oil;
    parts.push(`${Math.round(oil).toLocaleString()} bbl lifted → $${money.toLocaleString()}`);
  }

  if (cargo.ammo) {
    res.ammoReserve += cargo.ammo;
    parts.push(`${cargo.ammo} rounds`);
  }
  if (cargo.torpedoes) {
    res.torpedoes += cargo.torpedoes;
    parts.push(`${cargo.torpedoes} torpedo${cargo.torpedoes > 1 ? 'es' : ''}`);
  }
  if (cargo.depthCharges) {
    res.depthCharges += cargo.depthCharges;
    parts.push(`${cargo.depthCharges} depth charges`);
  }
  if (cargo.repairPercent) {
    res.integrity = clamp(res.integrity + cargo.repairPercent, 0, 100);
    parts.push(`+${cargo.repairPercent}% integrity`);
  }
  if (cargo.crew) {
    res.crew = Math.min(res.crewCapacity, res.crew + cargo.crew);
    parts.push(`+${cargo.crew} crew`);
  }

  return { money, oil, summary: parts.join(' · ') || 'nothing of value' };
}

/**
 * Losing a friendly. A tanker loss is a *lost sale*, not lost oil — the crude is still in
 * the rig's tanks, so the sting is the contract penalty plus the storage pressure that
 * follows. A supply ship loss forfeits the actual manifest, softened by insurance.
 */
export function applyLoss(res: Resources, cargo: Cargo | undefined): string {
  if (!cargo) return 'vessel lost';

  if (cargo.oilCollection) {
    res.money = Math.max(0, res.money - ECONOMY.tankerLossPenalty);
    return `contract penalty −$${ECONOMY.tankerLossPenalty.toLocaleString()} · cargo remains in storage`;
  }

  // Insurance refunds a fraction of what the manifest was worth.
  const value = manifestValue(cargo);
  const refund = Math.round(value * ECONOMY.insuranceRate);
  res.money += refund;
  return `manifest lost · insurance +$${refund.toLocaleString()}`;
}

/** What a manifest was worth, priced off `MANIFEST_ITEMS` so insurance tracks the shop. */
function manifestValue(cargo: Cargo): number {
  let v = 0;
  if (cargo.ammo) v += (cargo.ammo / 900) * 20000;
  if (cargo.torpedoes) v += cargo.torpedoes * 20000;
  if (cargo.depthCharges) v += (cargo.depthCharges / 4) * 6000;
  if (cargo.repairPercent) v += (cargo.repairPercent / 20) * 30000;
  if (cargo.crew) v += (cargo.crew / 12) * 40000;
  return v;
}

/** Rig damage, with the blast-plating upgrade applied. Returns crew casualties. */
export function applyRigDamage(
  res: Resources,
  damage: number,
  hasBlastPlating: boolean,
  casualtyRoll: number,
): number {
  const scaled = damage * (hasBlastPlating ? 0.75 : 1);
  res.integrity = clamp(res.integrity - scaled, 0, 100);

  // Heavy hits cost people. The crew number is not decoration: it throttles production
  // and, low enough, ends the campaign.
  let casualties = 0;
  if (scaled >= 3) {
    casualties = 1 + Math.floor(casualtyRoll * 3);
    res.crew = Math.max(0, res.crew - casualties);
  }
  return casualties;
}

/** The two campaign-ending conditions (plan A1). */
export function isDefeated(res: Resources): boolean {
  return res.integrity <= 0 || res.crew < ECONOMY.crewEvacuation;
}

/** Move rounds from the reserve into the magazine. Returns how many were loaded. */
export function reloadMagazine(res: Resources, magazineSize: number, current: number): number {
  const want = magazineSize - current;
  const got = Math.min(want, res.ammoReserve);
  res.ammoReserve -= got;
  return got;
}
